import { useState, useEffect } from 'react'
import api from '../services/api'
import { ENDPOINTS } from '../services/endpoints'

interface KbSyncStatus {
  healthy: boolean | null
  status: 'healthy' | 'unhealthy' | 'unknown'
}

interface BrainSummary {
  initialized: boolean
  total_engines?: number
  self_healing_enabled?: boolean
  online_learning_enabled?: boolean
}

interface DeploymentConfig {
  deployment_mode: 'hub' | 'client' | 'spoke' | 'standalone'
  is_hub: boolean
  is_spoke: boolean
  is_standalone: boolean
  kb_sync?: KbSyncStatus
  brain_summary: BrainSummary
  hub_console_url?: string
}

// Default to NOT hub — prevents Hub-only features flashing on Spoke before API responds.
// Once /health/deployment-mode responds, the real values take over.
const DEFAULT_CONFIG: DeploymentConfig = {
  deployment_mode: 'standalone',
  is_hub: false,
  is_spoke: false,
  is_standalone: true,
  brain_summary: { initialized: false },
  hub_console_url: '',
}

let _cachedConfig: DeploymentConfig | null = null

export function useDeploymentMode() {
  const [config, setConfig] = useState<DeploymentConfig>(_cachedConfig || DEFAULT_CONFIG)
  const [loading, setLoading] = useState(!_cachedConfig)

  useEffect(() => {
    if (_cachedConfig) return

    api.get(ENDPOINTS.HEALTH_DEPLOYMENT_MODE)
      .then(({ data }) => {
        _cachedConfig = data
        setConfig(data)
      })
      .catch(() => {
        // Keep default (standalone) — don't expose Hub features on error
        _cachedConfig = DEFAULT_CONFIG
      })
      .finally(() => setLoading(false))
  }, [])

  return {
    mode: config.deployment_mode,
    isHub: config.is_hub,
    isSpoke: config.is_spoke,
    isStandalone: config.is_standalone ?? false,
    kbSync: config.kb_sync,
    brainSummary: config.brain_summary,
    hubConsoleUrl: config.hub_console_url ?? '',
    loading,
  }
}
